import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';
import { LANGUAGE_SHORT_LABELS } from '../core/i18n/language-labels';
import { LANGUAGES } from '../core/i18n/languages';
import { switchLanguagePath } from '../core/i18n/switch-language-path';
import { useLanguageAlternates } from './language-alternates';
import { rememberLanguage } from './language-memory';

/**
 * The ҚАЗ / РУС pill pair, in the live site's shared treatment — the one thing `book.html`,
 * `policy.html` and `index.html` all draw the same way, which is why `language-switch.css` was
 * already a file of its own before any of them was ported.
 *
 * **It stays on the page the visitor is reading, query string and all.** The landing page's switch
 * can navigate to `/{lang}` because its own URL is the root; these two cannot. The booking wizard
 * keeps its step and its selections in the query string, so a switch that dropped it would send a
 * customer on step three back to an empty step one — the same defect that once emptied an
 * activation link, in the one other place it can still happen.
 */
export function LanguageSwitch() {
    const { t } = useTranslation();
    const current = useLanguage();
    const location = useLocation();
    const alternates = useLanguageAlternates();
    const path = `${location.pathname}${location.search}${location.hash}`;

    return (
        <div className="lang-switch" role="group" aria-label={t('site.nav.language')}>
            {LANGUAGES.map((language) => (
                <Link
                    key={language}
                    to={alternates?.[language] ?? switchLanguagePath(path, current, language)}
                    onClick={() => rememberLanguage(language)}
                    className={language === current ? 'active' : undefined}
                    aria-current={language === current ? 'true' : undefined}
                >
                    {LANGUAGE_SHORT_LABELS[language]}
                </Link>
            ))}
        </div>
    );
}
